
import React from "react";
import * as LucideIcons from "lucide-react";
import { AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";

// Define props for our SafeIcon component
export interface SafeIconProps {
  name: string;
  className?: string;
  size?: number | string;
  strokeWidth?: number;
  color?: string;
  fallback?: React.ReactNode;
  onClick?: () => void;
}

/**
 * A wrapper component that safely renders Lucide icons
 * Falls back to a placeholder if the icon doesn't exist
 */
export const SafeIcon: React.FC<SafeIconProps> = ({
  name,
  className,
  size = 24,
  strokeWidth = 2,
  color,
  fallback,
  onClick,
  ...rest
}) => {
  // Handle icon naming convention mappings
  const iconNameMap: Record<string, string> = {
    // Map problematic icons to their correct names
    "Trash": "Trash2",
    "CheckCircle": "CheckCircle2",
    "Building": "Building2",
    "Circle": "Circle", // Keep for reference
    // Add more mappings as needed
  };
  
  // Get the correct icon name if it exists in our map
  const correctedName = iconNameMap[name] || name;
  
  // Dynamic access to the icon component
  const IconComponent = (LucideIcons as any)[correctedName];

  // If the icon exists, render it
  if (IconComponent) {
    return (
      <IconComponent
        size={size}
        strokeWidth={strokeWidth}
        className={className}
        color={color}
        onClick={onClick}
        {...rest}
      />
    );
  }

  // If a custom fallback is provided, use it
  if (fallback) {
    return <>{fallback}</>;
  }

  // Default fallback - warning triangle with icon name as title
  return (
    <div className="relative inline-flex" title={`Icon '${name}' not found`}>
      <AlertTriangle
        size={size}
        strokeWidth={strokeWidth}
        className={cn("text-amber-500", className)}
        {...rest}
      />
      <span className="sr-only">Icon {name} not found</span>
    </div>
  );
};

export default SafeIcon;
